import { motion } from 'motion/react';
import { Card } from './ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { WeatherDataPoint } from '../utils/mockWeatherData';

interface WeatherStatsProps {
  data: WeatherDataPoint[];
}

interface StatItem {
  label: string;
  min: number;
  max: number;
  avg: number;
  unit: string;
}

export function WeatherStats({ data }: WeatherStatsProps) {
  const calculateStats = (values: number[]): { min: number; max: number; avg: number } => {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
    return { min, max, avg };
  };

  const stats: StatItem[] = [
    {
      label: 'Temperatura',
      ...calculateStats(data.map(d => d.aht_temp)),
      unit: '°C',
    },
    {
      label: 'Wilgotność',
      ...calculateStats(data.map(d => d.aht_hum)),
      unit: '%',
    },
    {
      label: 'Ciśnienie',
      ...calculateStats(data.map(d => d.bmp_pres)),
      unit: 'hPa',
    },
    {
      label: 'Wiatr',
      ...calculateStats(data.map(d => d.wind_speed)),
      unit: 'km/h',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      <Card className="p-6">
        <h3 className="mb-6">Statystyki (w zależności od wybranego okresu czasu)</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="space-y-3"
            >
              <h4 className="text-sm text-gray-600">{stat.label}</h4>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-red-500" />
                    Maks
                  </span>
                  <span className="text-red-600">
                    {stat.max.toFixed(2)} {stat.unit}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Minus className="w-3 h-3 text-gray-500" />
                    Średnia
                  </span>
                  <span className="text-gray-700">
                    {stat.avg.toFixed(2)} {stat.unit}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <TrendingDown className="w-3 h-3 text-blue-500" />
                    Min
                  </span>
                  <span className="text-blue-600">
                    {stat.min.toFixed(2)} {stat.unit}
                  </span>
                </div>
              </div>
              
              {/* Visual bar */}
              <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-500 via-gray-500 to-red-500"
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </Card>
    </motion.div>
  );
}
